import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, InlineNotification, Select, SelectItem, Tag, TextInput, Toggle } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { MoneyInput } from '../components/MoneyInput';
import type { Command } from './Commands';

// Bi-a 8 bong (chi tai khoan test): bot bat / tat va giay cho truoc khi bot vao phong, trinh do bot (de / vua / gioi: he A doc
// so cua tung trinh do tu config/bida.json), giay moi luot, khoang muc cuoc nguoi choi tu dat khi tao phong. Chang 3
// (docs/specs/bida-chang-3.md): tien cuoc that giu tu vi, phi san % tren tien thang rong, quy bot co tran (bat, von). Muc cuoc,
// phi san va quy bot chi chu so huu doi; bao cao tien (van, phi san, quy bot) chi chu so huu xem. Cong khai (docs/specs/bida-ship-an.md):
// tat = chi tai khoan test thay va choi bi-a; chi chu so huu bat / tat. Ban co san (docs/specs/bida-phong-san.md): cac muc cuoc
// sanh luon co mot ban cho moi muc; chi chu so huu doi. ID duoc vao (owner 2026-10-16): khi cong khai tat, cac id nguoi choi nay
// cung thay va choi bi-a; chi chu so huu doi.

type CapDo = 'de' | 'vua' | 'gioi' | 'caothu';
const CAP_DO: { v: CapDo; ten: string }[] = [
  { v: 'de', ten: 'Dễ: hay ngắm lệch, chỉ đánh cú dễ' },
  { v: 'vua', ten: 'Vừa: ngắm khá, biết đánh phòng thủ' },
  { v: 'gioi', ten: 'Giỏi: ngắm chuẩn, tính đường bi cái, phòng thủ' },
  { v: 'caothu', ten: 'Cao thủ: ngắm rất chuẩn, đánh kết hợp, phá bi an toàn (khó nhất)' },
];
type CauHinh = {
  cuoc: { min: number; max: number } | null; luotGiay: number; doNhayNgam: number; phe: number | null; quyBot: { bat: boolean; von: number };
  congKhai: boolean; choPhep: number[]; thangCuoc: number[]; banBot: { moiMuc: number; dangChoi: number; theoMuc?: Record<string, number | { 8: number; 9: number }> };
  bot: { bat: boolean; choGiay: number | null; capDo: CapDo };
  nhacNen?: { bat: boolean; tep: string }; nhacNenDs?: { tep: string; ten: string }[];
  nguon: 'cms' | 'mac_dinh'; phien_ban: number;
};
type Data = { cauHinh: CauHinh | null; loi?: string };
type Form = {
  botBat: boolean; choGiay: string; capDo: CapDo; luotGiay: string; doNhayNgam: string; nhacBat: boolean; nhacTep: string; cuocMin: string; cuocMax: string;
  phe: string; quyBat: boolean; quyVon: string; congKhai: boolean; choPhep: string; banMoiMuc: string; banDangChoi: string;
};
type Khoang = { ten: 'homNay' | 'bayNgay' | 'tatCa'; van: number; tongCuoc: number; phe: number; voiBot: number; hoan: number };
type DongVan = { phong: string; cuoc: number; phe: number; ty_le: number; thang_ghe: number; thang_uid: number | null; thua_uid: number | null;
  co_bot: number; ly_do: string | null; luc: number; ten0: string | null; ten1: string | null };
type BaoCao = {
  co: boolean; khoang: Khoang[]; dangGiu: { nguoi: number; bot: number; phong: number };
  quy: { co: boolean; so_du: number; von: number; trenBan: number; lai: number; khop: boolean; tongSo: number }; van: DongVan[];
};
const TEN_KHOANG: Record<Khoang['ten'], string> = { homNay: 'Hôm nay', bayNgay: '7 ngày', tatCa: 'Từ đầu' };
const LY_DO: Record<string, string> = { THANG: 'Vào bi 8', BI8_SOM: 'Bi 8 sớm', BI8_PHAM_LOI: 'Bi 8 kèm lỗi', BI8_SAI_LO: 'Bi 8 sai lỗ', BO_CUOC: 'Bỏ cuộc', HET_GIO: 'Hết giờ' };

// khoang so ban bot cua he A (api routes/admin/bida.ts)
const BAN_MOI_MUC_TOI_DA = 5;
const BAN_DANG_CHOI_TOI_DA = 30;
const CHO_PHEP_TOI_DA = 50;
// "1.000, 5.000 10000" -> [1000, 5000, 10000]; chi chu so, dau phay, khoang trang
const docThang = (s: string): number[] => s.split(/[,;\s]+/).map((x) => x.replace(/\D/g, '')).filter(Boolean).map(Number);
// bac ban bot va so ban rieng tung bac (task bida-ban-bot-5) sua o trang Ban bot bi-a (task bida-ban-bot-cms): o day chi xem
const vietBan = (v: number | { 8: number; 9: number }) => (typeof v === 'number' ? v + ' bàn' : v[8] + ' bàn 8 bóng, ' + v[9] + ' bàn 9 bóng');
const so = (s: string) => s.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '').slice(0, 14);
const toForm = (c: CauHinh): Form => ({
  botBat: c.bot.bat, choGiay: c.bot.choGiay != null ? String(c.bot.choGiay) : '', capDo: c.bot.capDo,
  luotGiay: String(c.luotGiay), doNhayNgam: String(c.doNhayNgam ?? 0.5),
  nhacBat: c.nhacNen?.bat ?? true, nhacTep: c.nhacNen?.tep ?? 'snd/lam-lang.mp3', cuocMin: c.cuoc ? String(c.cuoc.min) : '', cuocMax: c.cuoc ? String(c.cuoc.max) : '',
  phe: c.phe != null ? String(c.phe) : '', quyBat: c.quyBot.bat, quyVon: String(c.quyBot.von), congKhai: c.congKhai === true,
  choPhep: (c.choPhep ?? []).join(', '),
  banMoiMuc: String(c.banBot?.moiMuc ?? 0), banDangChoi: String(c.banBot?.dangChoi ?? 0)
});
const tienDoi = (a: Form, b: Form) => a.cuocMin !== b.cuocMin || a.cuocMax !== b.cuocMax || a.phe !== b.phe || a.quyBat !== b.quyBat || a.quyVon !== b.quyVon ||
  a.congKhai !== b.congKhai || JSON.stringify(docThang(a.choPhep)) !== JSON.stringify(docThang(b.choPhep)) ||
  a.banMoiMuc !== b.banMoiMuc || a.banDangChoi !== b.banDangChoi;

function loiForm(f: Form): string | null {
  const cho = Number(f.choGiay);
  if (f.botBat && (f.choGiay === '' || cho < 3 || cho > 600)) return 'Bật bot cần số giây chờ từ 3 đến 600';
  if (f.choGiay !== '' && (cho < 3 || cho > 600)) return 'Giây chờ bot phải từ 3 đến 600';
  if (!CAP_DO.some((c) => c.v === f.capDo)) return 'Chọn trình độ bot';
  const l = Number(f.luotGiay);
  if (f.luotGiay === '' || l < 10 || l > 300) return 'Giây mỗi lượt phải từ 10 đến 300';
  const nhay = Number(f.doNhayNgam);
  if (f.doNhayNgam === '' || !Number.isFinite(nhay) || nhay < 0.1 || nhay > 2) return 'Độ nhạy ngắm phải từ 0.1 đến 2';
  if ((f.cuocMin === '') !== (f.cuocMax === '')) return 'Nhập đủ cược tối thiểu và tối đa (hoặc để trống cả hai)';
  if (f.cuocMin !== '' && (Number(f.cuocMin) < 1 || Number(f.cuocMin) > Number(f.cuocMax))) return 'Cược tối thiểu phải từ 1 và không lớn hơn tối đa';
  if (f.phe === '' || Number(f.phe) < 0 || Number(f.phe) > 50) return 'Phí sàn phải từ 0 đến 50 (%)';
  if (f.quyVon === '') return 'Nhập vốn quỹ bot (0 nếu chưa cấp)';
  const cp = docThang(f.choPhep);
  if (cp.length > CHO_PHEP_TOI_DA) return 'Tối đa ' + CHO_PHEP_TOI_DA + ' ID được vào bi-a';
  if (cp.some((x) => x < 1) || new Set(cp).size !== cp.length) return 'ID được vào bi-a phải từ 1, không trùng';
  if (f.banMoiMuc === '' || Number(f.banMoiMuc) > BAN_MOI_MUC_TOI_DA) return 'Số bàn bot mỗi mức phải từ 0 đến ' + BAN_MOI_MUC_TOI_DA;
  if (f.banDangChoi === '' || Number(f.banDangChoi) > BAN_DANG_CHOI_TOI_DA) return 'Số bàn đang chơi phải từ 0 đến ' + BAN_DANG_CHOI_TOI_DA;
  return null;
}

export function BidaPage() {
  const { can, me } = useAuth();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['bida'], queryFn: () => api.get<Data>('/api/admin/games/bida'), refetchInterval: 10000 });
  const [form, setForm] = useState<Form | null>(null);
  const luu = useMutation({
    mutationFn: (f: Form) => api.put<{ command: Command }>('/api/admin/games/bida', {
      cuoc: f.cuocMin !== '' ? { min: Number(f.cuocMin), max: Number(f.cuocMax) } : null,
      luotGiay: Number(f.luotGiay),
      doNhayNgam: Number(f.doNhayNgam),
      nhacNen: { bat: f.nhacBat, tep: f.nhacTep },
      bot: { bat: f.botBat, choGiay: f.choGiay !== '' ? Number(f.choGiay) : null, capDo: f.capDo },
      ...(f.phe !== '' ? { phe: Number(f.phe) } : {}),
      quyBot: { bat: f.quyBat, von: Number(f.quyVon) },
      congKhai: f.congKhai,
      choPhep: docThang(f.choPhep),
      // bac ban bot va theoMuc: trang Ban bot bi-a; khong gui = may chu giu gia tri dang ap dung
      banBot: { moiMuc: Number(f.banMoiMuc), dangChoi: Number(f.banDangChoi) },
    }),
    onSuccess: (r) => nav('/commands/' + r.command.id),
  });

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={6} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const ch = q.data.cauHinh;
  const sua = can('game:control');
  const laChu = me?.role === 'founder';
  if (!ch) {
    return (
      <div className="page">
        <PageHeader title="Bi-a 8 bóng" lead="Cài đặt bot, lượt đánh và mức cược phòng bi-a." />
        <InlineNotification kind="warning" lowContrast hideCloseButton title="Kết nối máy game" subtitle="Hệ A chưa trả lời: chưa đọc được cài đặt bi-a." />
      </div>
    );
  }
  const f = form ?? toForm(ch);
  const doi = JSON.stringify(f) !== JSON.stringify(toForm(ch));
  const doiCuoc = tienDoi(f, toForm(ch));
  const loi = doi ? loiForm(f) : null;
  const dat = (p: Partial<Form>) => setForm({ ...f, ...p });

  return (
    <div className="page page--with-bar">
      <PageHeader title="Bi-a 8 bóng"
        lead="Sảnh có bàn bot sẵn theo các mức dưới đây (người chơi vào là đánh với bot); bàn người chơi tự tạo chỉ đánh người với người, chủ bàn mời bạn bè. Tiền cược hai bên giữ từ ví lúc vào bàn, người thắng nhận cả hai phần trừ phí sàn." />
      <div className="row health">
        <span>Cấu hình: {ch.nguon === 'mac_dinh' ? <span className="muted">chưa đặt từ CMS</span> : <>phiên bản <span className="num">{ch.phien_ban}</span></>}</span>
      </div>
      <div className="stack" style={{ maxWidth: 560 }}>
        <h3>Mở cho người chơi</h3>
        <Toggle id="bida-cong-khai" labelText="Mở bi-a cho mọi người chơi" labelA="Tắt" labelB="Bật" toggled={f.congKhai} disabled={!sua || !laChu}
          onToggle={(v: boolean) => dat({ congKhai: v })} />
        <p className="muted">
          {ch.congKhai ? 'Đang mở: mọi người chơi thấy và chơi được bi-a.' : 'Đang tắt: chỉ tài khoản test và các ID người chơi bên dưới thấy và chơi được bi-a; người chơi thật khác không thấy nút và bị từ chối.'}
          {' '}Tắt lại khi đang có người chơi trong phòng: họ đánh hết ván đó, không ai vào phòng mới được.{laChu ? null : ' Chỉ chủ sở hữu bật / tắt.'}
        </p>
        <TextInput id="bida-cho-phep" labelText="ID người chơi được vào khi chưa mở" disabled={!sua || !laChu} value={f.choPhep}
          helperText={'Khi đang tắt, các tài khoản này cũng thấy và chơi được bi-a bằng tiền thật (ngoài tài khoản test). ID người chơi, cách nhau bằng dấu phẩy, tối đa ' + CHO_PHEP_TOI_DA + '.'}
          onChange={(e) => dat({ choPhep: e.target.value.replace(/[^\d, ]/g, '').slice(0, 600) })} />
        <h3>Bot</h3>
        <Toggle id="bida-bot-bat" labelText="Bot đánh cùng người chơi" labelA="Tắt" labelB="Bật" toggled={f.botBat} disabled={!sua}
          onToggle={(v: boolean) => dat({ botBat: v })} />
        <TextInput id="bida-bot-cho" labelText="Giây chờ trước khi bot vào phòng" inputMode="numeric" disabled={!sua}
          helperText="Không còn dùng: bot chỉ ngồi ở bàn có sẵn (3 đến 600)." value={f.choGiay} onChange={(e) => dat({ choGiay: so(e.target.value).slice(0, 3) })} />
        <Select id="bida-bot-capdo" labelText="Trình độ bot" disabled={!sua} value={f.capDo}
          helperText="Áp dụng từ cú đánh tiếp theo của bot, kể cả ván đang chơi." onChange={(e) => dat({ capDo: e.target.value as CapDo })}>
          {CAP_DO.map((c) => <SelectItem key={c.v} value={c.v} text={c.ten} />)}
        </Select>
        <p className="muted">Tắt bot: phòng mới không có bot vào nữa; bot đang đánh dở vẫn đánh hết ván đó.</p>
        <h3>Quỹ bot</h3>
        <Toggle id="bida-quy-bat" labelText="Bot cược bằng quỹ nhà" labelA="Tắt" labelB="Bật" toggled={f.quyBat} disabled={!sua || !laChu}
          onToggle={(v: boolean) => dat({ quyBat: v })} />
        <MoneyInput id="bida-quy-von" labelText="Vốn quỹ (trần lỗ của nhà)" value={f.quyVon} disabled={!sua || !laChu}
          helperText="Bot đặt cược bằng quỹ này; quỹ không đủ mức cược của phòng thì bot không vào. Đổi vốn: số dư đổi đúng phần chênh."
          onChange={(v) => dat({ quyVon: v })} />
        <p className="muted">Bàn bot có sẵn chỉ hiện khi cả hai công tắc Bot và Quỹ bot đều bật; mức nào quỹ không đủ thì bàn mức đó ẩn.</p>
        <h3>Lượt đánh</h3>
        <TextInput id="bida-luot" labelText="Giây mỗi lượt" inputMode="numeric" disabled={!sua}
          helperText="Hết giờ là phạm lỗi (đối thủ được đặt bi tự do); hết giờ nhiều lượt liền thì thua (10 đến 300)." value={f.luotGiay} onChange={(e) => dat({ luotGiay: so(e.target.value).slice(0, 3) })} />
        <TextInput id="bida-nhay-ngam" labelText="Độ nhạy ngắm mặc định" inputMode="decimal" disabled={!sua}
          helperText="Hệ số tốc độ xoay gậy khi kéo ngắm (0.1 đến 2; mặc định 0.5 = nửa tốc độ cũ). Người chơi có thể chỉnh thêm trên máy trong Cài đặt."
          value={f.doNhayNgam} onChange={(e) => dat({ doNhayNgam: e.target.value.replace(/[^0-9.]/g, '').slice(0, 5) })} />
        <h3>Nhạc nền</h3>
        <Toggle id="bida-nhac-bat" labelText="Nhạc nền trong bàn bi-a" labelA="Tắt" labelB="Bật" toggled={f.nhacBat} disabled={!sua}
          onToggle={(v: boolean) => dat({ nhacBat: v })} />
        <Select id="bida-nhac-tep" labelText="Bài nhạc" disabled={!sua} value={f.nhacTep}
          helperText="Phát lặp, âm lượng nhỏ, sau lần chạm đầu tiên. Người chơi tự tắt được bằng nút nốt nhạc trên bàn; tắt ở đây thì không ai nghe và không tải bài nhạc."
          onChange={(e) => dat({ nhacTep: e.target.value })}>
          {(ch.nhacNenDs ?? [{ tep: f.nhacTep, ten: f.nhacTep }]).map((b) => <SelectItem key={b.tep} value={b.tep} text={b.ten} />)}
        </Select>
        <h3>Mức cược phòng</h3>
        <div className="row" style={{ gap: '1rem' }}>
          <TextInput id="bida-cuoc-min" labelText="Cược tối thiểu" inputMode="numeric" disabled={!sua || !laChu} value={f.cuocMin} onChange={(e) => dat({ cuocMin: so(e.target.value) })} />
          <TextInput id="bida-cuoc-max" labelText="Cược tối đa" inputMode="numeric" disabled={!sua || !laChu} value={f.cuocMax} onChange={(e) => dat({ cuocMax: so(e.target.value) })} />
        </div>
        <p className="muted">
          {ch.cuoc ? <>Đang áp dụng: {formatVnd(ch.cuoc.min)} đến {formatVnd(ch.cuoc.max)}.</> : <>Chưa đặt: người chơi chưa tạo được phòng.</>}
          {laChu ? null : ' Chỉ chủ sở hữu đổi được mức cược, phí sàn và quỹ bot.'}
        </p>
        <h3>Bàn có sẵn</h3>
        <p>
          {ch.thangCuoc.length ? <>Các mức có bàn bot: {ch.thangCuoc.map((x) => formatVnd(x)).join(', ')}.</> : <>Không có bàn có sẵn.</>}
          {' '}Thêm, bớt mức và số bàn từng mức ở trang <Link className="cell-link" to="/games/bida/ban-bot">Bàn bot bi-a</Link>.
        </p>
        <div className="row" style={{ gap: '1rem' }}>
          <TextInput id="bida-ban-moi-muc" labelText="Số bàn bot mỗi mức" inputMode="numeric" disabled={!sua || !laChu} value={f.banMoiMuc}
            helperText={'Mỗi mức, mỗi thể thức (0 đến ' + BAN_MOI_MUC_TOI_DA + ').'} onChange={(e) => dat({ banMoiMuc: so(e.target.value).slice(0, 1) })} />
          <TextInput id="bida-ban-dang-choi" labelText="Số bàn đang chơi (bot với bot)" inputMode="numeric" disabled={!sua || !laChu} value={f.banDangChoi}
            helperText={'Chỉ để sảnh đông vui, không có tiền, không ai vào được (0 đến ' + BAN_DANG_CHOI_TOI_DA + ').'} onChange={(e) => dat({ banDangChoi: so(e.target.value).slice(0, 2) })} />
        </div>
        <p className="muted">
          Đang áp dụng: {ch.banBot.moiMuc} bàn bot mỗi mức
          {Object.keys(ch.banBot.theoMuc ?? {}).length ? <> (riêng: {Object.entries(ch.banBot.theoMuc ?? {}).sort((a, b) => Number(a[0]) - Number(b[0])).map(([k, v]) => formatVnd(Number(k)) + ': ' + vietBan(v)).join(', ')})</> : null}
          , {ch.banBot.dangChoi} bàn đang chơi.{laChu ? null : ' Chỉ chủ sở hữu đổi được.'}
        </p>
        <h3>Phí sàn</h3>
        <TextInput id="bida-phe" labelText="Phí sàn (%)" inputMode="numeric" disabled={!sua || !laChu} value={f.phe}
          helperText="Tính trên tiền thắng ròng (tiền cược của người thua), làm tròn xuống. Ví dụ cược 100.000 và phí 5%: người thắng nhận 195.000. Từ 0 đến 50."
          onChange={(e) => dat({ phe: so(e.target.value).slice(0, 2) })} />
      </div>
      {laChu ? <BaoCaoTien /> : null}
      {doi ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span>Đã đổi, chưa lưu{loi ? <span className="muted">: {loi}</span> : doiCuoc && !laChu ? <span className="muted">: chỉ chủ sở hữu đổi được mức cược, bàn có sẵn, số bàn bot, phí sàn, quỹ bot, mở cho người chơi và ID được vào</span> : null}</span>
          <Button kind="ghost" size="md" onClick={() => { setForm(null); luu.reset(); }}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={!!loi || luu.isPending || (doiCuoc && !laChu)} onClick={() => luu.mutate(f)}>Lưu, áp dụng ngay</Button>
        </div>
      ) : null}
      <InlineError error={luu.error} />
    </div>
  );
}

// Bao cao tien bi-a (chu so huu): van, tong cuoc, phi san theo hom nay / 7 ngay / tu dau, tien dang giu, quy bot, 50 van gan nhat
function BaoCaoTien() {
  const q = useQuery({ queryKey: ['bida-bao-cao'], queryFn: () => api.get<BaoCao>('/api/admin/games/bida/bao-cao'), refetchInterval: 15000 });
  if (q.isLoading) return <BlockSkeleton lines={6} />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  return (
    <section aria-label="Báo cáo tiền bi-a" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '2rem', maxWidth: 1100 }}>
      <h3 style={{ margin: 0 }}>Báo cáo tiền</h3>
      {!d.co ? <p className="muted">Chưa có ván cược nào.</p> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
            {d.khoang.map((k) => (
              <div key={k.ten} style={{ padding: '1rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
                <div className="muted" style={{ fontSize: '0.8125rem' }}>{TEN_KHOANG[k.ten]}</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>{formatVnd(k.phe)}</div>
                <div className="muted" style={{ fontSize: '0.8125rem' }}>phí sàn từ <span className="num">{k.van}</span> ván, tổng cược {formatVnd(k.tongCuoc)}</div>
                <div className="muted" style={{ fontSize: '0.8125rem' }}><span className="num">{k.voiBot}</span> ván với bot, <span className="num">{k.hoan}</span> phòng hoàn tiền</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            <O ten="Quỹ bot: số dư" gia={d.quy.co ? formatVnd(d.quy.so_du) : 'Chưa lập'} />
            <O ten="Quỹ bot: vốn" gia={d.quy.co ? formatVnd(d.quy.von) : '-'} />
            <O ten="Quỹ bot: đang cược trên bàn" gia={formatVnd(d.quy.trenBan)} />
            <O ten="Quỹ bot: lãi / lỗ từ khi lập" gia={d.quy.co ? formatVnd(d.quy.lai) : '-'} mau={d.quy.lai < 0 ? 'var(--cds-support-error)' : undefined} />
            <O ten="Tiền người chơi đang giữ" gia={formatVnd(d.dangGiu.nguoi)} phu={d.dangGiu.phong + ' phòng đang mở'} />
          </div>
          {d.quy.co && !d.quy.khop ? (
            <InlineNotification kind="error" lowContrast hideCloseButton title="Sổ quỹ bot lệch"
              subtitle={`Số dư ${formatVnd(d.quy.so_du)} khác tổng sổ ${formatVnd(d.quy.tongSo)}.`} />
          ) : null}
          <div style={{ overflowX: 'auto' }}>
            <table aria-label="Ván bi-a gần nhất" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--cds-border-subtle)', textAlign: 'left' }}>
                  <th style={{ padding: '0.5rem' }}>Lúc</th>
                  <th style={{ padding: '0.5rem' }}>Người thắng</th>
                  <th style={{ padding: '0.5rem' }}>Người thua</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Cược</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right' }}>Phí sàn</th>
                  <th style={{ padding: '0.5rem' }}>Kết thúc</th>
                </tr>
              </thead>
              <tbody>
                {d.van.map((v) => {
                  const ten = [v.ten0 ?? '', v.ten1 ?? ''];
                  const thang = ten[v.thang_ghe] || '—';
                  const thua = ten[1 - v.thang_ghe] || '—';
                  const laBot = (ghe: number) => v.co_bot === 1 && (ghe === v.thang_ghe ? v.thang_uid : v.thua_uid) == null;
                  return (
                    <tr key={v.phong} style={{ borderBottom: '1px solid var(--cds-border-subtle)' }}>
                      <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{new Date(v.luc).toLocaleString('vi-VN')}</td>
                      <td style={{ padding: '0.5rem' }}>{thang}{v.thang_uid != null ? <span className="muted"> (#{v.thang_uid})</span> : null} {laBot(v.thang_ghe) ? <Tag size="sm" type="gray">bot</Tag> : null}</td>
                      <td style={{ padding: '0.5rem' }}>{thua}{v.thua_uid != null ? <span className="muted"> (#{v.thua_uid})</span> : null} {laBot(1 - v.thang_ghe) ? <Tag size="sm" type="gray">bot</Tag> : null}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatVnd(v.cuoc)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatVnd(v.phe)} <span className="muted">({v.ty_le}%)</span></td>
                      <td style={{ padding: '0.5rem' }}>{v.ly_do ? (LY_DO[v.ly_do] ?? v.ly_do) : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function O({ ten, gia, mau, phu }: { ten: string; gia: string; mau?: string; phu?: string }) {
  return (
    <div style={{ padding: '1rem', border: '1px solid var(--cds-border-subtle)', borderRadius: '6px' }}>
      <div className="muted" style={{ fontSize: '0.8125rem' }}>{ten}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: mau }}>{gia}</div>
      {phu ? <div className="muted" style={{ fontSize: '0.8125rem' }}>{phu}</div> : null}
    </div>
  );
}
